import { FC, useEffect, useState, useCallback } from "react";
import api from "../../api";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Checkbox from "@mui/material/Checkbox";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import Pagination from "@mui/material/Pagination";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import { tableColumns } from "../model/constants";
import DownloadIcon from "@mui/icons-material/Download";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import * as locales from "@mui/material/locale";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  StarryBackground,
  NebulaEffect,
  ContentContainer,
  HeaderContainer,
  TitleContainer,
  BackButton,
  StyledTableContainer,
  StyledDownloadButton,
  StyledSelect,
  StyledPagination,
  FooterContainer,
  StyledTypography,
} from "./styled";
import keycloak from "../../keycloak";
import { TextField } from "@mui/material";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";

const rmiacURL = process.env.REACT_APP_RMIAC as string;
const logoutURL = process.env.REACT_APP_PORTAL as string;

const getDispensaryComorbidRecordsURL =
  "DispensaryComorbid/GetDispensaryComorbidRecords";

const getDispensaryComorbidReportURL =
  "DispensaryComorbid/GetDispensaryComorbidReport";

const getLastUpdateDateURL = "DispensaryComorbid/GetLastUpdateDate";

const getDispensaryComorbidDeadStatURL =
  "DispensaryComorbid/GetDispensaryComorbidDeadStat";

const title = "Регистр коморбидных";

const yelolowColor = "rgba(36, 138, 197, 0.94)";

const DispensaryObservation: FC<{
  getFioAndOrganization: (value: string) => void;
}> = ({ getFioAndOrganization }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdateDate, setLastUpdateDate] = useState("");
  const [dispensaryArray, setDispensaryArray] = useState<
    | {
        expiredCount: number;
        list: any;
        totalCount: number;
      }
    | undefined
  >(undefined);
  const [page, setPage] = useState(1);
  const [isNeedRefetch, setIsNeedRefetch] = useState(false);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [liveAndDead, setLiveAndDead] = useState({
    deadCount: 0,
    liveCount: 0,
  });
  const [searchFIO, setSearchFIO] = useState("");
  const [debouncedSearchFIO, setDebouncedSearchFIO] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchFIO(searchFIO);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchFIO]);

  const autorizedPerson = localStorage.getItem("fioAndOrganization");

  const getDispensaryComorbidRecords = useCallback(() => {
    setIsLoading(true);

    api
      .get(
        `${getDispensaryComorbidRecordsURL}?Fullname=${debouncedSearchFIO}&Skip=${(page - 1) * rowsPerPage}&Take=${rowsPerPage}`,
      )
      .then((data) => {
        setDispensaryArray(data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          alert("Ошибка авторизации");
          console.log("error", err);
        } else {
          alert("Не удалось загрузить диспансерное наблюдение, " + err);
        }
        setIsLoading(false);
      });
  }, [page, rowsPerPage, debouncedSearchFIO]);

  useEffect(() => {
    getDispensaryComorbidRecords();
  }, [getDispensaryComorbidRecords]);

  useEffect(() => {
    document.title = title;

    getDispensaryComorbidRecords();

    setIsLoading(true);
    api
      .get(getLastUpdateDateURL)
      .then((data) => {
        setLastUpdateDate(data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          alert("Ошибка авторизации");
          console.log("error", err);
        } else {
          alert("Не удалось загрузить дату последнего обновления, " + err);
        }
        setIsLoading(false);
      });

    api
      .get(getDispensaryComorbidDeadStatURL)
      .then((data) => {
        setLiveAndDead(data.data);
        setIsLoading(false);
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          alert("Ошибка авторизации");
          console.log("error", err);
        } else {
          alert("Не удалось загрузить количество живых и умерших, " + err);
        }
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isNeedRefetch) {
      try {
        getDispensaryComorbidRecords();
      } catch (err) {
        console.log(err);
      }

      setIsNeedRefetch(false);
    }
  }, [isNeedRefetch, getDispensaryComorbidRecords]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setIsNeedRefetch(true);
  };

  const handleChangeRowsPerPage = (value: string) => {
    setRowsPerPage(parseInt(value, 10));
    setIsNeedRefetch(true);
    setPage(1);
  };

  const handleDownloadXlsx = () => {
    setIsDocumentLoading(true);

    try {
      api
        .get(getDispensaryComorbidReportURL, {
          responseType: "blob",
        })
        .then(({ headers, data }) => {
          const disposition = headers["content-disposition"];

          const blob = new Blob([data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          let fileName = "file.xlsx";

          if (disposition && disposition.indexOf("filename=") !== -1) {
            const matches = disposition.match(/filename="?([^"]+)"?/);
            if (matches != null && matches[1]) {
              fileName = matches[1];
            }
          }

          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(link.href); // очистка

          setIsDocumentLoading(false);
        })
        .catch((err) => {
          console.log(err);

          alert(
            "Ошибка авторизации, файл не удалось скачать, переавторизуйтесь пожалуйста",
          );

          setIsDocumentLoading(false);
        });
    } catch (err) {
      console.log(err);
    }
  };

  const handlePatientClick = (event: React.MouseEvent<unknown>, id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const theme = useTheme();

  const themeWithLocale = createTheme(theme, locales["ruRU"]);

  const handleExit = () => {
    localStorage.clear();
    getFioAndOrganization("");
    keycloak.login({
      redirectUri: logoutURL,
    });
  };

  const handleFIO = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchFIO(e.target.value);

  return (
    <StarryBackground>
      <NebulaEffect />
      <ThemeProvider theme={themeWithLocale}>
        <ContentContainer>
          <HeaderContainer>
            <BackButton onClick={handleExit} startIcon={<ArrowBackIcon />}>
              Назад
            </BackButton>
            <TitleContainer>
              <Typography
                color="white"
                variant="h1"
                sx={{
                  fontSize: "48px",
                  fontWeight: "bold",
                  textShadow: `
                                        0 0 10px rgba(59, 130, 246, 0.5),
                                        0 0 20px rgba(59, 130, 246, 0.3),
                                        0 0 30px rgba(59, 130, 246, 0.2)
                                    `,
                  animation: "titleGlow 3s ease-in-out infinite",
                  "@keyframes titleGlow": {
                    "0%, 100%": {
                      textShadow: `
                                                0 0 10px rgba(59, 130, 246, 0.5),
                                                0 0 20px rgba(59, 130, 246, 0.3),
                                                0 0 30px rgba(59, 130, 246, 0.2)
                                            `,
                    },
                    "50%": {
                      textShadow: `
                                                0 0 15px rgba(59, 130, 246, 0.8),
                                                0 0 25px rgba(59, 130, 246, 0.5),
                                                0 0 35px rgba(59, 130, 246, 0.3)
                                            `,
                    },
                  },
                }}
              >
                К
                <IconButton
                  href={rmiacURL}
                  target="_blank"
                  sx={{
                    padding: 0,
                    marginBottom: "10px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: "transparent",
                      transform: "scale(1.1)",
                      cursor: "pointer",
                    },
                  }}
                >
                  <img
                    src="/logo-cosmos.svg"
                    alt="custom icon"
                    style={{ width: 50, height: 50 }}
                  />
                </IconButton>
                СМОС
              </Typography>
            </TitleContainer>
            <Box display="flex" gap={2} alignItems="flex-end">
              <Typography
                color="white"
                variant="h3"
                sx={{
                  fontSize: "20px",
                  fontWeight: "normal",
                  textAlign: "center",
                  marginTop: "10px",
                  textShadow: `
                                        0 0 5px rgba(59, 130, 246, 0.3),
                                        0 0 10px rgba(59, 130, 246, 0.2)
                                    `,
                }}
              >
                Дата обновления регистра: {lastUpdateDate}
              </Typography>
              <Typography
                color="white"
                variant="h3"
                sx={{
                  fontSize: "24px",
                  fontWeight: "normal",
                  textAlign: "center",
                  marginTop: "10px",
                  textShadow: `
                                        0 0 5px rgba(59, 130, 246, 0.3),
                                        0 0 10px rgba(59, 130, 246, 0.2)
                                    `,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Box width="100px" /> {/* Spacer для балансировки */}
          </HeaderContainer>

          <TextField
            placeholder="Поиск по имени"
            variant="outlined"
            onChange={handleFIO}
            sx={{
              backgroundColor: "white",
              width: "600px",
              margin: "8px",
              borderRadius: "4px",
            }}
          />

          {isLoading ? (
            <StarryBackground>
              <NebulaEffect />
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                position="relative"
                zIndex={10}
              >
                <CircularProgress sx={{ color: "white" }} />
              </Box>
            </StarryBackground>
          ) : (
            <>
              <StyledTableContainer
                sx={{ maxHeight: "calc(100vh - 175px)", flex: 1 }}
              >
                <Table aria-label="sticky table">
                  <TableHead>
                    <TableRow
                      sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 100,
                      }}
                    >
                      {tableColumns.map(({ value, label }, index) => {
                        const getColumnWidth = (
                          index: number,
                          value: string,
                        ) => {
                          if (index === 0) return "160px";

                          // Булевы значения - узкие столбцы
                          if (
                            value.includes("has") ||
                            value.includes("achieved") ||
                            value.includes("therapy") ||
                            value.includes("socialservices") ||
                            value.includes("stableclinicaleffect")
                          )
                            return "250px";

                          // Возраст, баллы, номера - узкие столбцы
                          if (
                            value.includes("age") ||
                            value.includes("score") ||
                            value.includes("areanumber") ||
                            value.includes("emergencycalls")
                          )
                            return "120px";

                          // Пол - очень узкий столбец
                          if (value.includes("gender")) return "80px";

                          // Даты - средние столбцы
                          if (value.includes("date") || value.includes("Date"))
                            return "250px";

                          // АД, ЧСС, уровни - средние столбцы
                          if (
                            value.includes("pressure") ||
                            value.includes("heartrate") ||
                            value.includes("ldl") ||
                            value.includes("hba1c")
                          )
                            return "260px";

                          // Адрес - широкий столбец
                          if (value.includes("address")) return "350px";

                          // Остальные - средние столбцы
                          return "300px";
                        };

                        return (
                          <TableCell
                            sx={{
                              minWidth: getColumnWidth(index, value),
                              backgroundColor: "rgba(59, 130, 246)",
                            }}
                            key={value}
                          >
                            {index + 1}. {label}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* @ts-ignore */}
                    {dispensaryArray?.list.map((row, rowIndex) => {
                      const isItemSelected = selected.includes(rowIndex);
                      const isNotLastYearBSK = !row.lastYearBSKFollowUpDate;
                      const phoneNumberBackground = !row.phoneNumber
                        ? yelolowColor
                        : "";
                      const socialServicesBackground = "";
                      const lastYearBSKFollowUpDateBackground = isNotLastYearBSK
                        ? yelolowColor
                        : "";
                      const lastYearPMOOrDGVNBackground = !row.lastYearPMOOrDGVN
                        ? yelolowColor
                        : "";
                      const hasDispensaryI48Background =
                        !row.hasDispensaryI48 && row.hasI48 ? yelolowColor : "";
                      const i48NextPlannedVisitDateBackground =
                        row.i48NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const heartRateValue = row.actualHeartRate
                        .replace(/\(.*?\)/g, "")
                        .trim();
                      const actualHeartRateBackground =
                        row.actualHeartRate &&
                        (Number(heartRateValue) < 40 ||
                          Number(heartRateValue) > 100)
                          ? yelolowColor
                          : "";
                      const hasDispensaryI50Background =
                        !row.hasDispensaryI50 && row.hasI50 ? yelolowColor : "";
                      const i50NextPlannedVisitDateBackground =
                        row.i50NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryI10_I13Background =
                        row.hasI10_I13 && !row.hasDispensaryI10_I13
                          ? yelolowColor
                          : "";
                      const i10I13NextPlannedVisitDateBackground =
                        row.i10I13NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryI65_2Background =
                        !row.hasDispensaryI65_2 && row.hasI65_2
                          ? yelolowColor
                          : "";
                      const i652NextPlannedVisitDateBackground =
                        row.i652NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryI20_I25Background =
                        row.hasI20_I25 && !row.hasDispensaryI20_I25
                          ? yelolowColor
                          : "";
                      const i20I25NextPlannedVisitDateBackground =
                        row.i20I25NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryI26Background =
                        row.hasI26 && !row.hasDispensaryI26 ? yelolowColor : "";
                      const i26NextPlannedVisitDateBackground =
                        row.i26NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryE78Background =
                        row.hasE78 && !row.hasDispensaryE78 ? yelolowColor : "";
                      const e78NextPlannedVisitDateBackground =
                        row.e78NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const actualLDLMatch = row.actualLDL.match(
                        /-?(?:\d+(\.\d+)?|\.\d+)/,
                      );
                      const actualLDLResult = actualLDLMatch
                        ? parseFloat(actualLDLMatch[0])
                        : null;
                      const actualLDLBackground =
                        actualLDLResult !== null && actualLDLResult >= 1.4
                          ? yelolowColor
                          : "";
                      const hasDispensaryI21_I22Background =
                        row.hasHistoryI21_I22 && !row.hasDispensaryI21_I22
                          ? yelolowColor
                          : "";
                      const hasDispensaryI60_I64Background =
                        row.hasHistoryI60_I64 && !row.hasDispensaryI60_I64
                          ? yelolowColor
                          : "";
                      const hasDispensaryE10_E11Background =
                        row.hasE10_E11 && !row.hasDispensaryE10_E11
                          ? yelolowColor
                          : "";
                      const e10E11NextPlannedVisitDateBackground =
                        row.e10E11NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryJ44Background =
                        row.hasJ44 && !row.hasDispensaryJ44 ? yelolowColor : "";
                      const j44NextPlannedVisitDateBackground =
                        row.j44NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const hasDispensaryN18Background =
                        row.hasN18 && !row.hasDispensaryN18 ? yelolowColor : "";
                      const n18NextPlannedVisitDateBackground =
                        row.n18NextPlannedVisitDate.includes(
                          "пропущена плановая явка",
                        )
                          ? yelolowColor
                          : "";
                      const anticoagulantTherapyBackground =
                        !row.anticoagulantTherapy &&
                        row.hasI48 &&
                        ((row.gender === "м" &&
                          Number(row.chA2DS2VAScScore) >= 2) ||
                          (row.gender === "ж" &&
                            Number(row.chA2DS2VAScScore) >= 3))
                          ? yelolowColor
                          : "";

                      const isAteroskleroz =
                        row.hasI20_I25 ||
                        row.hasE78 ||
                        row.hasHistoryI21_I22 ||
                        row.hasHistoryI60_I64 ||
                        row.pciDate ||
                        row.cabgDate ||
                        row.atherosclerosisOperations;

                      let threshold = null;

                      if (row.age >= 18 && row.age <= 44) {
                        threshold = isAteroskleroz ? 6.5 : 7;
                      } else if (row.age <= 59) {
                        threshold = isAteroskleroz ? 7 : 7.5;
                      } else if (row.age <= 74) {
                        threshold = isAteroskleroz ? 7.5 : 8;
                      } else if (row.age <= 90) {
                        threshold = 8;
                      } else {
                        threshold = 8.5;
                      }

                      const actualHbA1cBackground =
                        threshold !== null && Number(String(row.actualHbA1c.replace(",", ".")))>= threshold
                          ? yelolowColor
                          : "";

                      // Color map keyed by field name
                      const cellColorMap: Record<string, string> = {
                        phoneNumber: phoneNumberBackground,
                        socialServices: socialServicesBackground,
                        lastYearBSKFollowUpDate:
                          lastYearBSKFollowUpDateBackground,
                        lastYearPMOOrDGVN: lastYearPMOOrDGVNBackground,
                        hasDispensaryI48: hasDispensaryI48Background,
                        i48NextPlannedVisitDate:
                          i48NextPlannedVisitDateBackground,
                        actualHeartRate: actualHeartRateBackground,
                        anticoagulantTherapy: anticoagulantTherapyBackground,
                        hasDispensaryI50: hasDispensaryI50Background,
                        i50NextPlannedVisitDate:
                          i50NextPlannedVisitDateBackground,
                        hasDispensaryI10_I13: hasDispensaryI10_I13Background,
                        i10I13NextPlannedVisitDate:
                          i10I13NextPlannedVisitDateBackground,
                        hasDispensaryI65_2: hasDispensaryI65_2Background,
                        i652NextPlannedVisitDate:
                          i652NextPlannedVisitDateBackground,
                        hasDispensaryI20_I25: hasDispensaryI20_I25Background,
                        i20I25NextPlannedVisitDate:
                          i20I25NextPlannedVisitDateBackground,
                        hasDispensaryI26: hasDispensaryI26Background,
                        i26NextPlannedVisitDate:
                          i26NextPlannedVisitDateBackground,
                        hasDispensaryE78: hasDispensaryE78Background,
                        e78NextPlannedVisitDate:
                          e78NextPlannedVisitDateBackground,
                        actualLDL: actualLDLBackground,
                        hasDispensaryI21_I22: hasDispensaryI21_I22Background,
                        hasDispensaryI60_I64: hasDispensaryI60_I64Background,
                        hasDispensaryE10_E11: hasDispensaryE10_E11Background,
                        e10E11NextPlannedVisitDate:
                          e10E11NextPlannedVisitDateBackground,
                        hasDispensaryJ44: hasDispensaryJ44Background,
                        j44NextPlannedVisitDate:
                          j44NextPlannedVisitDateBackground,
                        hasDispensaryN18: hasDispensaryN18Background,
                        n18NextPlannedVisitDate:
                          n18NextPlannedVisitDateBackground,
                        actualHbA1c: actualHbA1cBackground,
                      };

                      // Build array of keys/values excluding internal fields
                      const excludedKeys = new Set(["moBindId", "isSoc"]);
                      const rowEntries = Object.entries(row).filter(
                        ([key]) => !excludedKeys.has(key),
                      );

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={rowIndex}
                          onClick={(event) =>
                            handlePatientClick(event, rowIndex)
                          }
                          aria-checked={isItemSelected}
                          selected={isItemSelected}
                          sx={{
                            backgroundColor: isItemSelected
                              ? "rgb(35, 76, 143) !important"
                              : row.registryExclusionDate
                                ? "#aeaeae !important"
                                : "",
                          }}
                        >
                          {rowEntries.map(([key, value], index) => {
                            const isFirstElem = key === "fullName";

                            const getColumnWidth = (
                              index: number,
                              key: string,
                            ) => {
                              // ФИО - самый широкий столбец
                              if (index === 0) return "100px";

                              // Булевы значения - узкие столбцы
                              if (
                                key.includes("has") ||
                                key.includes("achieved") ||
                                key.includes("therapy") ||
                                key.includes("socialservices") ||
                                key.includes("stableclinicaleffect")
                              )
                                return "100px";
                              // Возраст, баллы, номера - узкие столбцы
                              if (
                                key.includes("age") ||
                                key.includes("score") ||
                                key.includes("areanumber") ||
                                key.includes("emergencycalls")
                              )
                                return "120px";

                              // Пол - очень узкий столбец
                              if (key.includes("gender")) return "80px";

                              // Даты - средние столбцы
                              if (key.includes("date") || key.includes("Date"))
                                return "150px";

                              // АД, ЧСС, уровни - средние столбцы
                              if (
                                key.includes("pressure") ||
                                key.includes("heartrate") ||
                                key.includes("ldl") ||
                                key.includes("hba1c")
                              )
                                return "160px";

                              // Адрес - широкий столбец
                              if (key.includes("address")) return "250px";

                              // Остальные - средние столбцы
                              return "180px";
                            };

                            return (
                              <TableCell
                                // @ts-ignore
                                key={value + index + key}
                                sx={{
                                  minWidth: getColumnWidth(index, key),
                                  verticalAlign: "middle",
                                  position: isFirstElem ? "sticky" : "static",
                                  left: isFirstElem ? 0 : "auto",
                                  zIndex: isFirstElem ? 5 : "auto",
                                  backgroundColor: row.registryExclusionDate
                                    ? "#aeaeae"
                                    : isFirstElem
                                      ? isItemSelected
                                        ? "rgb(35, 76, 143) !important"
                                        : "rgba(59, 130, 246)"
                                      : value ===
                                            "рассмотреть усиление гипотензивной терапии" ||
                                          value ===
                                            "рассмотреть уменьшение гипотензивной терапии"
                                        ? yelolowColor
                                        : cellColorMap[key] || "transparent",

                                  backdropFilter: isFirstElem
                                    ? "blur(10px)"
                                    : "none",
                                }}
                              >
                                {isFirstElem &&
                                  cellColorMap["lastYearBSKFollowUpDate"] ===
                                    yelolowColor &&
                                  !row.registryExclusionDate && (
                                    <Box
                                      display="flex"
                                      position="fixed"
                                      top={8}
                                      right={8}
                                    >
                                      <WarningAmberIcon color="error" />
                                    </Box>
                                  )}
                                {isFirstElem && (
                                  <>
                                    {rowIndex + 1 + rowsPerPage * (page - 1)}
                                    <Checkbox
                                      sx={{ ml: 1 }}
                                      color="primary"
                                      checked={isItemSelected}
                                    />
                                  </>
                                )}

                                {value ===
                                "рассмотреть усиление гипотензивной терапии" ? (
                                  <Typography alignItems="center">
                                    <VerticalAlignTopIcon
                                      fontSize="small"
                                      sx={{ marginRight: 1 }}
                                    />
                                    {value as string}
                                  </Typography>
                                ) : value ===
                                  "рассмотреть уменьшение гипотензивной терапии" ? (
                                  <Typography alignItems="center">
                                    <VerticalAlignBottomIcon
                                      fontSize="small"
                                      sx={{ marginRight: 1 }}
                                    />
                                    {value as string}
                                  </Typography>
                                ) : (
                                  (value as string)
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </StyledTableContainer>

              <FooterContainer>
                <StyledDownloadButton
                  onClick={handleDownloadXlsx}
                  startIcon={
                    isDocumentLoading ? (
                      <CircularProgress size={20} sx={{ color: "white" }} />
                    ) : (
                      <DownloadIcon />
                    )
                  }
                  disabled={isDocumentLoading}
                  sx={{ width: 190 }}
                >
                  {isDocumentLoading ? "Загружаю xlsx" : "Загрузить xlsx"}
                </StyledDownloadButton>
                <Box display="flex" flexDirection="column">
                  <StyledTypography>
                    Общее количество записей: {dispensaryArray?.totalCount || 0}
                  </StyledTypography>
                  <StyledTypography>
                    Количество живых/умерших: {liveAndDead.liveCount} /{" "}
                    {liveAndDead.deadCount}
                  </StyledTypography>
                </Box>

                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={1}
                >
                  <StyledTypography>Строк на странице:</StyledTypography>
                  <StyledSelect
                    size="small"
                    variant="outlined"
                    value={rowsPerPage}
                    onChange={(e) =>
                      handleChangeRowsPerPage(
                        (e.target as HTMLSelectElement).value,
                      )
                    }
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </StyledSelect>
                </Box>

                <StyledPagination>
                  <Pagination
                    count={Math.ceil(
                      (dispensaryArray?.totalCount || 0) / rowsPerPage,
                    )}
                    page={page}
                    onChange={(event, value) => handleChangePage(value)}
                    color="primary"
                    size="large"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        color: "white",
                        border: "1px solid gray",
                        "&:hover": {
                          backgroundColor: "rgba(59, 130, 246, 0.2)",
                          borderColor: "gray",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(59, 130, 246)",
                          borderColor: "gray",
                        },
                      },
                    }}
                  />
                </StyledPagination>
              </FooterContainer>
              {autorizedPerson && (
                <Paper
                  sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "20px",
                    display: "flex",
                    justifyContent: "end",
                    paddingRight: 3,
                    alignItems: "center",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="caption">{autorizedPerson}</Typography>
                </Paper>
              )}
            </>
          )}
        </ContentContainer>
      </ThemeProvider>
    </StarryBackground>
  );
};

export default DispensaryObservation;
